
import React, { useEffect } from 'react';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";

interface OtpInputProps {
  length?: number;
  onOtpComplete?: (otp: string) => void;
  onResendOtp: () => void;
  isResendDisabled?: boolean;
  resendCountdown?: number;
  expectedOtp?: string;
  autoFill?: boolean;
  onOtpChange?: (otp: string) => void;
}

const OtpInput: React.FC<OtpInputProps> = ({
  length = 6,
  onOtpComplete,
  onResendOtp,
  isResendDisabled = false,
  resendCountdown = 0,
  expectedOtp,
  autoFill = false,
  onOtpChange
}) => {
  const [otp, setOtp] = React.useState("");

  // Auto-fill OTP when expectedOtp changes and autoFill is enabled
  useEffect(() => {
    if (autoFill && expectedOtp && expectedOtp.length === length) {
      console.log("Auto-filling OTP with:", expectedOtp);
      setOtp(expectedOtp);
      
      // Also notify parent component of the change
      if (onOtpChange) {
        onOtpChange(expectedOtp);
      }
      
      // Remove auto-submission since we want the user to validate manually
    }
  }, [expectedOtp, length, autoFill, onOtpChange]);

  // Check for stored OTP for testing purposes
  useEffect(() => {
    const storedOtp = localStorage.getItem('adminOtpCode');
    if (storedOtp && autoFill) {
      console.log("Using stored OTP from localStorage:", storedOtp);
      setOtp(storedOtp);
      
      // Notify parent component about OTP change
      if (onOtpChange) {
        onOtpChange(storedOtp);
      }
      
      // Remove auto-submission
    }
  }, [autoFill, onOtpChange]);

  const handleValueChange = (value: string) => {
    console.log("OTP input changed to:", value);
    setOtp(value);
    
    // Notify parent component about OTP change
    if (onOtpChange) {
      onOtpChange(value);
    }
    
    // Auto-complete functionality when user manually enters the full code
    if (value.length === length && onOtpComplete) {
      console.log("OTP input complete, length matches expected length");
      // We don't auto-submit here anymore to give user a chance to review
      // and use the validate button instead
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col items-center justify-center space-y-4">
        <InputOTP
          maxLength={length}
          value={otp}
          onChange={handleValueChange}
          className="gap-2"
        >
          {Array.from({ length }).map((_, index) => (
            <InputOTPGroup key={index} className="min-w-[40px]">
              <InputOTPSlot index={index} className="rounded-md border-blue-300 focus:border-blue-500 h-12 w-12 text-lg" />
            </InputOTPGroup>
          ))}
        </InputOTP>
        
        <div className="mt-4">
          <Button
            variant="outline"
            onClick={onResendOtp}
            disabled={isResendDisabled}
            type="button"
            className="text-sm"
          >
            {isResendDisabled 
              ? `Resend code (${resendCountdown}s)` 
              : "Resend code"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OtpInput;
